import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  numberAttribute,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IllustrationRegistryService } from './illustration-registry.service';
import {
  getIllustrationSize,
  ILLUSTRATION_COLOR_SCHEMES,
  IllustrationAnimation,
  IllustrationColorScheme,
  IllustrationSize,
  IllustrationVariant,
} from './illustration.types';

@Component({
  selector: 'forma-illustration',
  templateUrl: './illustration.html',
  styleUrls: ['./illustration.scss', './illustration-animations.scss'],
  host: {
    class: 'forma-illustration',
    '[attr.data-illustration-name]': 'name()',
    '[attr.data-variant]': 'variant()',
    '[attr.data-color-scheme]': 'colorScheme()',
    '[attr.data-animation]': 'animation()',
    '[style.--illustration-animation-iteration]':
      'animationIteration() === 0 ? "infinite" : animationIteration()',
    '[attr.role]': '"img"',
    '[attr.aria-label]': 'ariaLabel()',
    '[attr.aria-hidden]': 'ariaHidden()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormaIllustration {
  name = input.required<string>();
  variant = input<IllustrationVariant>('plain');
  size = input<IllustrationSize>('medium');
  colorScheme = input<IllustrationColorScheme>('pine');
  animation = input<IllustrationAnimation>();
  animationIteration = input(0, { transform: numberAttribute });
  ariaLabel = input<string | undefined>(undefined);
  ariaHidden = input<boolean>(false);

  protected numericSize = computed(() => getIllustrationSize(this.size()));

  protected svgContent = signal<SafeHtml>('');

  constructor(
    private registry: IllustrationRegistryService,
    private elementRef: ElementRef<HTMLElement>,
    private sanitizer: DomSanitizer,
  ) {
    effect(() => {
      const illustrationName = this.name();
      const variant = this.variant();
      const scheme = this.colorScheme();

      const svgData = this.registry.getIllustration(illustrationName);
      if (!svgData) {
        console.warn(
          `Illustration "${illustrationName}" not found in registry. Did you forget to register it?`,
        );
        this.svgContent.set('');
        return;
      }

      const colors = ILLUSTRATION_COLOR_SCHEMES[scheme];
      const content = this.buildSvgContent(svgData, variant, colors.foreground, colors.background);
      this.svgContent.set(this.sanitizer.bypassSecurityTrustHtml(content));
    });

    effect(() => {
      const element = this.elementRef.nativeElement;
      const size = this.numericSize();
      element.style.setProperty('--illustration-size', `${size}px`);
    });
  }

  private buildSvgContent(
    shapeData: string,
    variant: IllustrationVariant,
    foreground: string,
    background: string,
  ): string {
    switch (variant) {
      case 'plain':
        return this.buildPlain(shapeData, foreground, background);
      case 'gradient':
        return this.buildGradient(shapeData, foreground, background);
      case 'transparent':
        return this.buildTransparentPlain(shapeData, background);
      case 'transparent-solid':
        return this.buildTransparentPlain(shapeData, foreground);
      case 'transparent-gradient':
        return this.buildTransparentGradient(shapeData, foreground, background);
    }
  }

  private buildPlain(shapeData: string, foreground: string, background: string): string {
    const bg = `<rect width="1080" height="1080" rx="54" fill="${foreground}"/>`;
    const shapes = shapeData
      .replace(/fill="white"/g, `fill="${background}"`)
      .replace(/stroke="white"/g, `stroke="${background}"`);
    return `${bg}${shapes}`;
  }

  private buildNoiseFilter(id: string, background: string): string {
    return `
        <filter id="${id}" filterUnits="userSpaceOnUse" x="-540" y="-540" width="2160" height="2160" color-interpolation-filters="sRGB">
          <feTurbulence type="fractalNoise" baseFrequency="2" numOctaves="3" result="noise" seed="8543"/>
          <feColorMatrix in="noise" type="saturate" values="0" result="monoNoise"/>
          <feComponentTransfer in="monoNoise" result="subtleNoise">
            <feFuncA type="linear" slope="0.9" intercept="0"/>
          </feComponentTransfer>
          <feFlood flood-color="${background}" result="colorFlood"/>
          <feComposite operator="in" in2="subtleNoise" in="colorFlood" result="coloredNoise"/>
          <feComposite operator="in" in2="SourceGraphic" in="coloredNoise" result="clippedNoise"/>
          <feMerge>
            <feMergeNode in="SourceGraphic"/>
            <feMergeNode in="clippedNoise"/>
          </feMerge>
        </filter>`;
  }

  private buildGradient(shapeData: string, foreground: string, background: string): string {
    const defs = `
      <defs>
        <linearGradient id="illustration-bg-gradient" x1="0" y1="1080" x2="1080" y2="0" gradientUnits="userSpaceOnUse">
          <stop stop-color="${foreground}"/>
          <stop offset="1" stop-color="${background}"/>
        </linearGradient>
        ${this.buildNoiseFilter('illustration-noise', background)}
      </defs>`;

    const bg = `
      <rect width="1080" height="1080" rx="54" fill="white"/>
      <rect width="1080" height="1080" rx="54" fill="url(#illustration-bg-gradient)" filter="url(#illustration-noise)"/>`;

    return `${defs}${bg}${shapeData}`;
  }

  private buildTransparentPlain(shapeData: string, foreground: string): string {
    return shapeData
      .replace(/fill="white"/g, `fill="${foreground}"`)
      .replace(/stroke="white"/g, `stroke="${foreground}"`);
  }

  private buildTransparentGradient(
    shapeData: string,
    foreground: string,
    background: string,
  ): string {
    const defs = `
      <defs>
        <linearGradient id="illustration-shape-gradient" x1="0" y1="1080" x2="1080" y2="0" gradientUnits="userSpaceOnUse">
          <stop stop-color="${foreground}"/>
          <stop offset="1" stop-color="${background}"/>
        </linearGradient>
        ${this.buildNoiseFilter('illustration-noise', background)}
      </defs>`;

    const shapes = shapeData
      .replace(/fill="white"/g, 'fill="url(#illustration-shape-gradient)"')
      .replace(/stroke="white"/g, 'stroke="url(#illustration-shape-gradient)"');

    return `${defs}<g filter="url(#illustration-noise)">${shapes}</g>`;
  }
}
